import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  useReactTable, getCoreRowModel, flexRender,
} from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "../components/ui/sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Target, Plus, Search, Kanban, TableProperties, Loader2, Pencil, Trash2, ArrowRight, MessageCircle, Phone, Mail, Building2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, X, ExternalLink, FileText, StickyNote, Send, ThumbsDown, Check, Clock, Flame, Snowflake, Zap, Layers, LayoutGrid, MoreHorizontal, Eye, User } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useAppearance } from "../contexts/AppearanceContext";
import CustomFieldsForm from "../components/CustomFieldsForm";
import { fmtDate, fmtDateShort } from "../components/negocio-detail/NegocioHelpers";
import { usePermissions } from "../hooks/usePermissions";
import WhatsAppChat from "../components/WhatsAppChat";
import LeadNoteEditor from "../components/LeadNoteEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "novo", label: "Novo", color: "#3b82f6" },
  { value: "contactado", label: "Contactado", color: "#f59e0b" },
  { value: "qualificado", label: "Qualificado", color: "#10b981" },
  { value: "convertido", label: "Convertido", color: "#10b981" },
  { value: "descartado", label: "Descartado", color: "#ef4444" },
];

const KANBAN_COLUMNS = [
  { key: "novo", label: "Novo", color: "#3b82f6" },
  { key: "contactado", label: "Contactado", color: "#f59e0b" },
  { key: "qualificado", label: "Qualificado", color: "#10b981" },
];

function StatusBadge({ status }) {
  const s = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold text-white" style={{ backgroundColor: s.color }}>{s.label}</span>;
}

function isValidEmail(email) {
  return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function LeadsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { can } = usePermissions();
  const appearance = useAppearance();
  const tc = appearance.colors?.["500"] || "#3B82F6";

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("kanban");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("aberto");

  // Table state
  const TABLE_PAGE_SIZE = 50;
  const [tablePage, setTablePage] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const tableTotalPages = Math.ceil(tableTotal / TABLE_PAGE_SIZE) || 1;
  const [pipelines, setPipelines] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [waUnread, setWaUnread] = useState({});

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [form, setForm] = useState({ nome: "", whatsapp: "", email: "", empresa: "", responsavel_id: "", custom_fields: {} });
  const [saving, setSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Detail modal
  const [detailLead, setDetailLead] = useState(null);
  const [detailTab, setDetailTab] = useState("resumo");

  // Notes
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [deleteNoteTarget, setDeleteNoteTarget] = useState(null);

  // Metrics
  const [metrics, setMetrics] = useState(null);

  // Delete / Convert
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertPipeline, setConvertPipeline] = useState("");
  const [existingDeal, setExistingDeal] = useState(null);
  const [conversionPreview, setConversionPreview] = useState(null);
  const [discardTarget, setDiscardTarget] = useState(null);
  const [reopenTarget, setReopenTarget] = useState(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  // Real-time: refresh when SSE emits lead_changed
  useEffect(() => {
    const handler = () => fetchLeads();
    window.addEventListener("pipe4:lead_changed", handler);
    return () => window.removeEventListener("pipe4:lead_changed", handler);
  }, [fetchLeads]);

  useEffect(() => {
    Promise.all([
      fetchLeads(),
      axios.get(`${API}/pipelines`).then(r => setPipelines(r.data)).catch(() => {}),
      axios.get(`${API}/users/responsaveis`).then(r => setResponsaveis(r.data)).catch(() => {}),
      axios.get(`${API}/custom-fields?module=leads`).then(r => setCustomFields(r.data)).catch(() => {}),
      axios.get(`${API}/whatsapp/lead-unread-counts`).then(r => setWaUnread(r.data || {})).catch(() => {}),
    ]);
  }, [fetchLeads]);

  // Deep link: /leads?open={id}
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || loading) return;
    const open = async () => {
      let lead = leads.find(l => l.id === openId);
      if (!lead) {
        try { const res = await axios.get(`${API}/leads/${openId}`, { withCredentials: true }); lead = res.data; } catch {}
      }
      if (lead) openDetail(lead);
      else setSearchParams({ open: openId }, { replace: true });
    };
    open();
  }, [searchParams, loading]); // eslint-disable-line


  // Refresh unread counts on SSE
  useEffect(() => {
    const handler = () => {
      axios.get(`${API}/whatsapp/lead-unread-counts`).then(r => setWaUnread(r.data || {})).catch(() => {});
    };
    window.addEventListener("pipe4:lead_changed", handler);
    return () => window.removeEventListener("pipe4:lead_changed", handler);
  }, []);

  const fetchNotes = useCallback(async (leadId) => {
    try {
      const res = await axios.get(`${API}/leads/${leadId}/notes`);
      setNotes(res.data);
    } catch { setNotes([]); }
  }, []);

  const fmt = fmtDateShort;
  const fmtFull = fmtDate;

  const formatPhone = (v) => {
    const digits = (v || "").replace(/\D/g, "");
    // Strip country code (55) if present
    const local = (digits.length >= 12 && digits.startsWith("55")) ? digits.slice(2) : digits;
    if (local.length <= 2) return local;
    const ddd = local.slice(0, 2);
    const num = local.slice(2);
    if (num.length >= 9) return `(${ddd}) ${num.slice(0, 5)}-${num.slice(5)}`;
    if (num.length >= 5) return `(${ddd}) ${num.slice(0, 4)}-${num.slice(4)}`;
    return `(${ddd}) ${num}`;
  };

  // Table fetch (server-side pagination)
  const fetchTable = useCallback(async () => {
    if (viewMode !== "table") return;
    setTableLoading(true);
    try {
      const params = new URLSearchParams({ limit: TABLE_PAGE_SIZE, skip: tablePage * TABLE_PAGE_SIZE });
      if (searchQuery) params.set("search", searchQuery);
      if (statusFilter === "convertido") params.set("status", "convertido");
      else if (statusFilter === "descartado") params.set("status", "descartado");
      // "aberto" and "todos" are handled by not passing specific status (backend returns all, we filter)
      const res = await axios.get(`${API}/leads?${params}`);
      let data = res.data.data || [];
      if (statusFilter === "aberto") data = data.filter(l => l.status !== "convertido" && l.status !== "descartado");
      setTableData(data);
      setTableTotal(res.data.total || 0);
    } catch { setTableData([]); setTableTotal(0); }
    setTableLoading(false);
  }, [viewMode, tablePage, searchQuery, statusFilter]);

  useEffect(() => { fetchTable(); }, [fetchTable]);

  const tableColumns = useMemo(() => [
    { id: "nome", accessorKey: "nome", header: "Nome do contato", size: 220, cell: ({ row }) => <span className="font-semibold text-slate-800">{row.original.nome}</span> },
    { id: "whatsapp", accessorKey: "whatsapp", header: "WhatsApp", size: 140, cell: ({ row }) => <span className="text-xs text-slate-600">{row.original.whatsapp ? formatPhone(row.original.whatsapp) : <span className="text-slate-300">—</span>}</span> },
    { id: "email", accessorKey: "email", header: "Email", size: 180, cell: ({ row }) => <span className="text-xs text-slate-600">{row.original.email || <span className="text-slate-300">—</span>}</span> },
    { id: "empresa", accessorKey: "empresa", header: "Empresa", size: 160, cell: ({ row }) => <span className="text-xs text-slate-600">{row.original.empresa || <span className="text-slate-300">—</span>}</span> },
    { id: "status", accessorKey: "status", header: "Status", size: 100, cell: ({ row }) => <StatusBadge status={row.original.status} /> },
    { id: "responsavel", accessorKey: "responsavel_nome", header: "Responsável", size: 140, cell: ({ row }) => <span className="text-xs text-slate-600">{row.original.responsavel_nome || <span className="text-slate-300">—</span>}</span> },
    { id: "created_at", accessorKey: "created_at", header: "Criado em", size: 130, cell: ({ row }) => <span className="text-xs text-slate-500">{fmt(row.original.created_at)}</span> },
    { id: "actions", header: "", size: 80, enableSorting: false, cell: ({ row }) => (
      <div className="flex items-center justify-end gap-0.5">
        {can("leads", "edit") && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); openEdit(row.original); }}><Pencil className="w-3.5 h-3.5 text-slate-400" /></Button>}
        {can("leads", "delete") && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); setDeleteTarget(row.original); }}><Trash2 className="w-3.5 h-3.5 text-rose-400" /></Button>}
      </div>
    )},
  ], [can, fmt, formatPhone]);

  const table = useReactTable({ data: tableData, columns: tableColumns, getCoreRowModel: getCoreRowModel(), manualPagination: true, pageCount: tableTotalPages });

  useEffect(() => {
    if (detailLead?.id && detailTab === "anotacoes") fetchNotes(detailLead.id);
  }, [detailLead?.id, detailTab, fetchNotes]);

  // Fetch metrics when detail opens or lead changes
  useEffect(() => {
    if (detailLead?.id) {
      axios.get(`${API}/leads/${detailLead.id}/metrics`).then(r => setMetrics(r.data)).catch(() => setMetrics(null));
    } else {
      setMetrics(null);
    }
  }, [detailLead?.id]);

  const filtered = leads.filter(l => {
    if (statusFilter === "aberto" && (l.status === "convertido" || l.status === "descartado")) return false;
    if (statusFilter === "convertido" && l.status !== "convertido") return false;
    if (statusFilter === "descartado" && l.status !== "descartado") return false;
    // "todos" shows everything
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (l.nome || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q) || (l.whatsapp || "").includes(q) || (l.empresa || "").toLowerCase().includes(q);
    }
    return true;
  });

  const openNew = () => {
    setEditLead(null);
    setForm({ nome: "", whatsapp: "", email: "", empresa: "", responsavel_id: "", custom_fields: {} });
    setFormOpen(true);
  };

  const openEdit = (lead) => {
    setEditLead(lead);
    setForm({ nome: lead.nome, whatsapp: lead.whatsapp, email: lead.email, empresa: lead.empresa, responsavel_id: lead.responsavel_id || "", custom_fields: lead.custom_fields || {} });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) { toast.error("Nome do contato é obrigatório"); return; }
    if (form.email && !isValidEmail(form.email)) { toast.error("Email inválido. Verifique o formato."); return; }
    setSaving(true);
    try {
      if (editLead) {
        await axios.put(`${API}/leads/${editLead.id}`, form);
        toast.success("Lead atualizado!");
      } else {
        await axios.post(`${API}/leads`, form);
        toast.success("Lead criado!");
      }
      setFormOpen(false);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/leads/${deleteTarget.id}`);
      toast.success("Lead removido");
      fetchLeads();
      if (detailLead?.id === deleteTarget.id) setDetailLead(null);
    } catch { toast.error("Erro ao remover"); }
    setDeleteTarget(null);
  };

  const handleConvert = async () => {
    if (!convertTarget || (!convertPipeline && !existingDeal)) { toast.error("Selecione uma pipeline"); return; }
    setConverting(true);
    try {
      const payload = existingDeal ? { existing_negocio_id: existingDeal.negocio_id } : { pipeline_id: convertPipeline };
      await axios.post(`${API}/leads/${convertTarget.id}/convert`, payload);
      toast.success(existingDeal ? "Lead vinculado ao negócio existente!" : "Lead convertido em negócio!");
      fetchLeads();
      setDetailLead(null);
      setConvertTarget(null);
      setExistingDeal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro na conversão");
    }
    setConverting(false);
  };

  const handleDiscard = async () => {
    if (!discardTarget) return;
    try {
      await axios.put(`${API}/leads/${discardTarget.id}`, { status: "descartado" });
      toast.success("Lead descartado");
      fetchLeads();
      if (detailLead?.id === discardTarget.id) setDetailLead(null);
    } catch { toast.error("Erro"); }
    setDiscardTarget(null);
  };

  const handleReopen = async () => {
    if (!reopenTarget) return;
    try {
      await axios.put(`${API}/leads/${reopenTarget.id}`, { status: "novo" });
      toast.success("Lead reaberto!");
      fetchLeads();
      const u = await axios.get(`${API}/leads/${reopenTarget.id}`);
      setDetailLead(u.data);
      setForm({ nome: u.data.nome, whatsapp: u.data.whatsapp || "", email: u.data.email || "", empresa: u.data.empresa || "", responsavel_id: u.data.responsavel_id || "", status: u.data.status || "novo", custom_fields: u.data.custom_fields || {} });
    } catch { toast.error("Erro"); }
    setReopenTarget(null);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !detailLead) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/leads/${detailLead.id}/notes`, { corpo_html: newNote });
      setNewNote("");
      fetchNotes(detailLead.id);
      toast.success("Anotação adicionada");
    } catch { toast.error("Erro ao salvar anotação"); }
    setSavingNote(false);
  };

  const openDetail = (lead) => {
    setDetailLead(lead);
    setDetailTab("resumo");
    setSearchParams({ open: lead.id }, { replace: true });
    // Initialize form with lead data for Editar tab
    setForm({ nome: lead.nome, whatsapp: lead.whatsapp || "", email: lead.email || "", empresa: lead.empresa || "", responsavel_id: lead.responsavel_id || "", status: lead.status || "novo", custom_fields: lead.custom_fields || {} });
  };

  const navigateLead = (direction) => {
    if (!detailLead) return;
    const colLeads = filtered.filter(l => l.status === detailLead.status);
    const idx = colLeads.findIndex(l => l.id === detailLead.id);
    const next = direction === "up" ? colLeads[idx - 1] : colLeads[idx + 1];
    if (next) { setDetailLead(next); setSearchParams({ open: next.id }, { replace: true }); setForm({ nome: next.nome, whatsapp: next.whatsapp || "", email: next.email || "", empresa: next.empresa || "", responsavel_id: next.responsavel_id || "", status: next.status || "novo", custom_fields: next.custom_fields || {} }); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const isClosed = detailLead?.status === "convertido" || detailLead?.status === "descartado";

  const DETAIL_TABS = [
    { key: "resumo", label: "Resumo", icon: FileText },
    ...(!isClosed ? [{ key: "editar", label: "Editar", icon: Pencil }] : []),
    { key: "anotacoes", label: "Anotações", icon: StickyNote },
    ...(detailLead?.whatsapp ? [{ key: "whatsapp", label: "WhatsApp", icon: MessageCircle, badge: waUnread[detailLead?.id] || 0 }] : []),
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header — identical to NegociosPage */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tc}18` }}><Target className="w-5 h-5" style={{ color: tc }} strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              Leads
              <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{filtered.length}</Badge>
            </h1>
            <p className="text-slate-500 mt-0.5">Gerencie seus leads e converta em negócios.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar leads..." className="pl-9 w-full sm:w-48 h-9 text-sm" />
          </div>
          {/* View toggle — single button like Negocios */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon"
                onClick={() => setViewMode(v => v === "kanban" ? "table" : "kanban")}
                className="h-9 w-9 border-2 transition-colors"
                style={{ borderColor: tc, color: tc }}>
                {viewMode === "kanban" ? <Kanban className="w-4 h-4" /> : <TableProperties className="w-4 h-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{viewMode === "kanban" ? "Kanban → Tabela" : "Tabela → Kanban"}</TooltipContent>
          </Tooltip>
          {/* Status cycle button — like Negocios */}
          {(() => {
            const STATUS_CYCLE = ["aberto", "convertido", "descartado", "todos"];
            const STATUS_CONFIG = {
              aberto:     { icon: Layers,     label: "Em aberto",   borderColor: tc,        iconColor: tc },
              convertido: { icon: ArrowRight,  label: "Convertidos", borderColor: "#10b981", iconColor: "#10b981" },
              descartado: { icon: ThumbsDown,  label: "Descartados", borderColor: "#ef4444", iconColor: "#ef4444" },
              todos:      { icon: LayoutGrid,  label: "Todos",       borderColor: tc,        iconColor: tc },
            };
            const cfg = STATUS_CONFIG[statusFilter] || STATUS_CONFIG["aberto"];
            const IconC = cfg.icon;
            const nextIdx = (STATUS_CYCLE.indexOf(statusFilter) + 1) % STATUS_CYCLE.length;
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon"
                    onClick={() => setStatusFilter(STATUS_CYCLE[nextIdx])}
                    className="h-9 w-9 border-2 bg-white transition-colors hover:bg-slate-50"
                    style={{ borderColor: cfg.borderColor, color: cfg.iconColor }}>
                    <IconC className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{cfg.label} → Clique para {STATUS_CONFIG[STATUS_CYCLE[nextIdx]]?.label}</TooltipContent>
              </Tooltip>
            );
          })()}
          {can("leads", "create") && (
            <Button onClick={openNew} size="icon" className="h-9 w-9 text-white" style={{ backgroundColor: tc }}><Plus className="w-4 h-4" /></Button>
          )}
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === "kanban" && (
        <div className="flex gap-4 overflow-x-auto pb-4 max-w-full flex-1" style={{ minHeight: 0 }}>
          {KANBAN_COLUMNS.map(col => {
            // Show leads in this status column + converted/discarded that were last in this status
            let colLeads = filtered.filter(l => l.status === col.key);
            // For converted/discarded view: show them in the column of their last active status
            if (statusFilter === "convertido" || statusFilter === "descartado" || statusFilter === "todos") {
              const finalized = filtered.filter(l => (l.status === "convertido" || l.status === "descartado"));
              finalized.forEach(l => {
                const history = l.status_history || [];
                let lastActive = "novo";
                for (let i = history.length - 1; i >= 0; i--) {
                  if (history[i].status !== "convertido" && history[i].status !== "descartado") {
                    lastActive = history[i].status;
                    break;
                  }
                }
                if (lastActive === col.key && !colLeads.find(x => x.id === l.id)) {
                  colLeads.push(l);
                }
              });
            }
            // Sort: WhatsApp unread first, then by most recently updated
            colLeads.sort((a, b) => {
              const waA = waUnread[a.id] || 0;
              const waB = waUnread[b.id] || 0;
              if (waA !== waB) return waB - waA;
              return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
            });
            return (
              <div key={col.key} className="flex-shrink-0 w-72 bg-slate-100/70 rounded-xl flex flex-col"
                onDragOver={e => e.preventDefault()}
                onDrop={async e => {
                  e.preventDefault();
                  const leadId = e.dataTransfer.getData("lead_id");
                  if (!leadId || col.key === "convertido" || col.key === "descartado") return;
                  try {
                    await axios.put(`${API}/leads/${leadId}`, { status: col.key });
                    fetchLeads();
                    toast.success(`Status alterado para ${col.label}`);
                  } catch { toast.error("Erro ao mover lead"); }
                }}>
                <div className="p-3 border-b border-slate-200/50">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: col.color }} /><span className="text-sm font-semibold text-slate-800">{col.label}</span><Badge className="ml-auto bg-white text-slate-600 border-0 text-xs">{colLeads.length}</Badge></div>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                  {colLeads.map(lead => (
                    <div key={lead.id} onClick={() => openDetail(lead)}
                      draggable={lead.status !== "convertido" && lead.status !== "descartado"}
                      onDragStart={e => e.dataTransfer.setData("lead_id", lead.id)}
                      className="bg-white rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
                      style={{ border: lead.status === "convertido" ? "2px solid #10b981" : lead.status === "descartado" ? "2px solid #ef4444" : "1px solid #e2e8f0" }}>
                      <div className="flex items-start justify-between mb-1.5">
                        <span className="text-sm font-semibold text-slate-800 truncate flex-1">{lead.nome}</span>
                        {(lead.status === "convertido" || lead.status === "descartado") && <StatusBadge status={lead.status} />}
                      </div>
                      {lead.empresa && <p className="text-xs text-slate-500 flex items-center gap-1 mb-1"><Building2 className="w-3 h-3" />{lead.empresa}</p>}
                      {lead.whatsapp && <p className="text-xs text-slate-500 flex items-center gap-1 mb-1"><Phone className="w-3 h-3" />{formatPhone(lead.whatsapp)}</p>}
                      {lead.email && <p className="text-xs text-slate-500 flex items-center gap-1 mb-1"><Mail className="w-3 h-3" />{lead.email}</p>}
                      <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-100">
                        <span className="text-[10px] text-slate-400">{fmtFull(lead.created_at)}</span>
                        <div className="flex items-center gap-1.5">
                          {waUnread[lead.id] > 0 && (
                            <Tooltip><TooltipTrigger asChild>
                              <div className="relative w-5 h-5 flex items-center justify-center cursor-default">
                                <span className="absolute inset-0 rounded-full animate-ping opacity-40 bg-green-400" />
                                <div className="relative w-5 h-5 rounded-full flex items-center justify-center bg-green-500">
                                  <MessageCircle className="w-3 h-3 text-white" />
                                </div>
                              </div>
                            </TooltipTrigger><TooltipContent className="text-xs">{waUnread[lead.id]} mensagem{waUnread[lead.id] !== 1 ? "s" : ""} não lida{waUnread[lead.id] !== 1 ? "s" : ""} no WhatsApp</TooltipContent></Tooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {colLeads.length === 0 && <div className="text-center py-8 text-slate-300 text-xs">Nenhum lead</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {viewMode === "table" && (
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: `${table.getVisibleFlatColumns().reduce((s, c) => s + c.getSize(), 0)}px` }}>
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id} className="border-b border-slate-200 bg-slate-50/80">
                  {hg.headers.map(header => (
                    <th key={header.id} style={{ width: header.getSize() }}
                      className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap overflow-hidden text-ellipsis">
                      <div className="flex items-center gap-1.5">{flexRender(header.column.columnDef.header, header.getContext())}</div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableLoading ? [...Array(8)].map((_, i) => (<tr key={i} className="border-b border-slate-100">{tableColumns.map((c, j) => (<td key={j} className="px-3 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} /></td>))}</tr>))
              : table.getRowModel().rows.length === 0 ? (<tr><td colSpan={tableColumns.length} className="text-center py-16"><div className="flex flex-col items-center text-slate-400"><Target className="w-10 h-10 mb-3 text-slate-300" /><p className="text-sm">Nenhum lead encontrado.</p></div></td></tr>)
              : table.getRowModel().rows.map(row => (
                <tr key={row.id} className="border-b border-slate-100 transition-colors cursor-pointer hover:bg-slate-50/50" onClick={() => openDetail(row.original)}>
                  {row.getVisibleCells().map(cell => (<td key={cell.id} className="px-3 py-2.5 overflow-hidden whitespace-nowrap text-ellipsis">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>))}
                </tr>))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
          <p className="text-xs text-slate-500">{tableTotal > 0 ? `${tablePage * TABLE_PAGE_SIZE + 1}-${Math.min((tablePage + 1) * TABLE_PAGE_SIZE, tableTotal)} de ${tableTotal}` : "Nenhum resultado"}</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={tablePage === 0} onClick={() => setTablePage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            {[...Array(Math.min(tableTotalPages, 5))].map((_, i) => { let pn; if (tableTotalPages <= 5) pn = i; else if (tablePage < 3) pn = i; else if (tablePage > tableTotalPages - 4) pn = tableTotalPages - 5 + i; else pn = tablePage - 2 + i; return (<Button key={pn} variant={pn === tablePage ? "default" : "outline"} size="sm" className={`h-8 w-8 p-0 text-xs ${pn === tablePage ? "text-white" : ""}`} style={pn === tablePage ? { backgroundColor: tc } : {}} onClick={() => setTablePage(pn)}>{pn + 1}</Button>); })}
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={tablePage >= tableTotalPages - 1} onClick={() => setTablePage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editLead ? "Editar Lead" : "Novo Lead"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div><Label>Nome do contato *</Label><Input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>WhatsApp</Label><Input value={formatPhone(form.whatsapp)} onChange={e => setForm({ ...form, whatsapp: e.target.value.replace(/\D/g, "") })} placeholder="(00) 00000-0000" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" /></div>
            </div>
            <div><Label>Empresa</Label><Input value={form.empresa} onChange={e => setForm({ ...form, empresa: e.target.value })} /></div>
            <div>
              <Label>Responsável</Label>
              <Select value={form.responsavel_id || "__none__"} onValueChange={v => setForm({ ...form, responsavel_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nenhum</SelectItem>
                  {responsaveis.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {customFields.length > 0 && (
              <CustomFieldsForm fields={customFields} values={form.custom_fields} onChange={cf => setForm({ ...form, custom_fields: cf })} />
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="text-white" style={{ backgroundColor: tc }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {editLead ? "Salvar" : "Criar Lead"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      {detailLead && (<>
        <div className="fixed inset-0 z-40 bg-black/80" />
        <Dialog open={!!detailLead} onOpenChange={() => {}} modal={false}>
          <DialogContent className="max-w-3xl !p-0 [&>button.absolute]:hidden !border-0" style={{ overflow: "visible" }} onOpenAutoFocus={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <div className="relative h-[75vh]">
              {/* Floating buttons */}
              {detailLead.status !== "convertido" && detailLead.status !== "descartado" && can("leads", "edit") && (
                <div className="hidden md:flex flex-col absolute -right-14 top-1/2 -translate-y-1/2 z-50">
                  {/* Quick actions group */}
                  <div className="flex flex-col gap-2">
                    <Tooltip delayDuration={300}><TooltipTrigger asChild>
                      <button onClick={() => { setDetailTab("anotacoes"); setNoteEditorOpen(true); }}
                        className="w-10 h-10 rounded-full bg-white border border-white shadow-lg flex items-center justify-center hover:scale-110 transition-all" style={{ color: tc }}>
                        <StickyNote className="w-4 h-4" />
                      </button>
                    </TooltipTrigger><TooltipContent side="left">Nova Anotação</TooltipContent></Tooltip>
                    {detailLead.whatsapp && (
                      <Tooltip delayDuration={300}><TooltipTrigger asChild>
                        <button onClick={() => setDetailTab("whatsapp")}
                          className="w-10 h-10 rounded-full bg-emerald-500 shadow-lg flex items-center justify-center text-white hover:bg-emerald-600 hover:scale-110 transition-all">
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      </TooltipTrigger><TooltipContent side="left">WhatsApp</TooltipContent></Tooltip>
                    )}
                    {detailLead.whatsapp && window.__goto_connected && (
                      <Tooltip delayDuration={300}><TooltipTrigger asChild>
                        <button data-testid="lead-quick-phone-btn" onClick={() => {
                          let phone = (detailLead.whatsapp || "").replace(/\D/g, "");
                          // Strip country code 55 if present
                          if (phone.length >= 12 && phone.startsWith("55")) phone = phone.slice(2);
                          if (phone) {
                            window.dispatchEvent(new CustomEvent("pipe4:dial", { detail: { phone, lead_id: detailLead.id } }));
                          } else {
                            toast.error("Lead sem telefone cadastrado.");
                          }
                        }}
                          className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-110 transition-all"
                          style={{ backgroundColor: "#f97316" }}>
                          <Phone className="w-4 h-4" />
                        </button>
                      </TooltipTrigger><TooltipContent side="left">Ligar</TooltipContent></Tooltip>
                    )}
                  </div>
                  {/* Status actions group — separated */}
                  <div className="flex flex-col gap-2 mt-[100px]">
                    <Tooltip><TooltipTrigger asChild>
                      <button onClick={async () => {
                        const current = STATUS_OPTIONS.findIndex(s => s.value === detailLead.status);
                        const next = STATUS_OPTIONS[current + 1];
                        if (next && next.value !== "convertido" && next.value !== "descartado") {
                          try { await axios.put(`${API}/leads/${detailLead.id}`, { status: next.value }); const u = await axios.get(`${API}/leads/${detailLead.id}`); setDetailLead(u.data); fetchLeads(); toast.success(`Status: ${next.label}`); } catch { toast.error("Erro"); }
                        }
                      }} className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center text-white transition-all hover:scale-110 hover:brightness-110" style={{ backgroundColor: tc }}>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </TooltipTrigger><TooltipContent side="left">Avançar status</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild>
                      <button onClick={async () => {
                        const current = STATUS_OPTIONS.findIndex(s => s.value === detailLead.status);
                        const prev = STATUS_OPTIONS[current - 1];
                        if (prev) { try { await axios.put(`${API}/leads/${detailLead.id}`, { status: prev.value }); const u = await axios.get(`${API}/leads/${detailLead.id}`); setDetailLead(u.data); fetchLeads(); toast.success(`Status: ${prev.label}`); } catch { toast.error("Erro"); } }
                      }} disabled={STATUS_OPTIONS.findIndex(s => s.value === detailLead.status) <= 0}
                        className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center text-white transition-all hover:scale-110 disabled:opacity-30" style={{ backgroundColor: tc }}>
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                    </TooltipTrigger><TooltipContent side="left">Retroceder status</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild>
                      <button onClick={() => setDiscardTarget(detailLead)}
                        className="w-10 h-10 rounded-full bg-rose-500 shadow-lg flex items-center justify-center text-white hover:bg-rose-600 hover:scale-110 transition-all">
                        <ThumbsDown className="w-4 h-4" />
                      </button>
                    </TooltipTrigger><TooltipContent side="left">Descartar</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild>
                      <button onClick={async () => {
                        if (detailLead.status !== "qualificado") {
                          toast.error("O lead precisa estar na etapa Qualificado para ser convertido.");
                          return;
                        }
                        if (!detailLead.empresa || !detailLead.empresa.trim()) {
                          toast.error("Preencha o campo Empresa antes de converter.");
                          return;
                        }
                        // Check for existing deal
                        try {
                          const check = await axios.get(`${API}/leads/${detailLead.id}/check-existing-deal`);
                          setExistingDeal(check.data.exists ? check.data : null);
                        } catch { setExistingDeal(null); }
                        setConvertTarget(detailLead); setConvertPipeline(pipelines[0]?.id || "");
                      }}
                        className="w-10 h-10 rounded-full bg-emerald-500 shadow-lg flex items-center justify-center text-white hover:bg-emerald-600 hover:scale-110 transition-all">
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </TooltipTrigger><TooltipContent side="left">Converter em Negócio</TooltipContent></Tooltip>
                  </div>
                </div>
              )}
              {/* Reopen button for converted/discarded — removed from floating, now only in ... menu */}
              {/* Modal content */}
              <div className="flex flex-col h-full overflow-hidden rounded-lg">

              {/* Header — identical pattern to NegocioDetailModal */}
              <div className="flex items-center gap-3 px-3 sm:px-5 py-4 sm:py-5 flex-shrink-0 flex-wrap rounded-t-lg" style={{ background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)" }}>
                <div className="flex items-stretch gap-3 flex-1 min-w-0">
                  <div className="flex items-center justify-center flex-shrink-0">
                    <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Target className="w-5 h-5 text-white" strokeWidth={1.8} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h2 className="text-base font-bold text-white font-heading leading-tight">{detailLead.nome}</h2>
                      {(() => {
                        const s = STATUS_OPTIONS.find(o => o.value === detailLead.status) || STATUS_OPTIONS[0];
                        const badgeBg = detailLead.status === "convertido" ? "#10b981" : detailLead.status === "descartado" ? "#ef4444" : "rgba(255,255,255,0.2)";
                        return (
                          <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold text-white flex-shrink-0" style={{ backgroundColor: badgeBg }}>
                            {s.label}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs flex-wrap">
                      {detailLead.responsavel_nome && <span className="flex items-center gap-1 text-white/80"><Pencil className="w-3 h-3" />{detailLead.responsavel_nome}</span>}
                      {detailLead.empresa && (
                        <span className="inline-flex items-center gap-1 border border-white/30 rounded px-1.5 py-0.5 text-white/90">
                          <Building2 className="w-3 h-3" />{detailLead.empresa}
                        </span>
                      )}
                      {detailLead.whatsapp && (
                        <span className="inline-flex items-center gap-1 border border-white/30 rounded px-1.5 py-0.5 text-white/90">
                          <Phone className="w-3 h-3" />{formatPhone(detailLead.whatsapp)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-stretch gap-1.5 flex-shrink-0">
                  {/* More actions dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="w-7 h-7 border-white/30 bg-white/10 hover:bg-white/20 text-white">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {(detailLead.status === "convertido" || detailLead.status === "descartado") && (
                        <>
                          <DropdownMenuItem onClick={() => setReopenTarget(detailLead)} className="cursor-pointer text-amber-600 focus:text-amber-700 focus:bg-amber-50">
                            <Layers className="w-4 h-4 mr-2" />Reabrir lead
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem onClick={() => setViewAllOpen(true)} className="cursor-pointer">
                        <Eye className="w-4 h-4 mr-2 text-slate-500" />Visualizar Dados
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setDeleteTarget(detailLead)} className="cursor-pointer text-rose-600 focus:text-rose-700 focus:bg-rose-50">
                        <Trash2 className="w-4 h-4 mr-2" />Excluir lead
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <button onClick={() => { setDetailLead(null); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/20 text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                {/* Tabs inside header */}
                <div className="flex gap-1 mt-3 w-full">
                  {DETAIL_TABS.map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button key={tab.key} onClick={() => setDetailTab(tab.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${detailTab === tab.key ? "bg-white/25 text-white" : "text-white/60 hover:text-white hover:bg-white/10"}`}>
                        <Icon className="w-3.5 h-3.5" />{tab.label}
                        {tab.badge > 0 && (
                          <span className="ml-1 w-4 h-4 bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-pulse">{tab.badge}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className={`flex-1 min-h-0 ${detailTab === "whatsapp" ? "overflow-hidden flex flex-col" : "overflow-hidden flex flex-col"}`}>
                {/* Sub-header — same style as WhatsApp contact header */}
                {(() => {
                  const tabInfo = {
                    resumo: { icon: FileText, label: "Resumo" },
                    editar: { icon: Pencil, label: "Editar" },
                    anotacoes: { icon: StickyNote, label: "Anotações" },
                    whatsapp: { icon: MessageCircle, label: "WhatsApp" },
                  };
                  const t = tabInfo[detailTab];
                  if (!t) return null;
                  const Icon = t.icon;
                  return (
                    <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 flex-shrink-0">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white" style={{ backgroundColor: "#1e293b" }}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 leading-tight">{t.label}</p>
                      </div>
                      {detailTab === "anotacoes" && !isClosed && (
                        <button type="button" onClick={() => { setEditingNote(null); setNoteEditorOpen(true); }}
                          className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors">
                          <Plus className="w-3 h-3" />Nova anotação
                        </button>
                      )}
                      {detailLead?.updated_at && (
                        <Tooltip delayDuration={300}><TooltipTrigger asChild>
                          <div className="flex items-center text-slate-400 cursor-default">
                            <Clock className="w-3.5 h-3.5" />
                          </div>
                        </TooltipTrigger><TooltipContent side="left">Última atualização: {fmtFull(detailLead.updated_at)}</TooltipContent></Tooltip>
                      )}
                    </div>
                  );
                })()}

                {/* Tab content — scrollable */}
                <div className={`flex-1 min-h-0 ${detailTab === "whatsapp" ? "overflow-hidden flex flex-col" : "overflow-y-auto p-6"}`}>

                {detailTab === "resumo" && (
                  <div className="space-y-4">
                    {/* Status Timeline */}
                    <div className="flex items-center justify-center gap-0 pb-2">
                      {(() => {
                        const steps = [
                          { value: "novo", label: "Novo" },
                          { value: "contactado", label: "Contactado" },
                          { value: "qualificado", label: "Qualificado" },
                        ];
                        if (detailLead.status === "convertido") steps.push({ value: "convertido", label: "Convertido" });
                        if (detailLead.status === "descartado") steps.push({ value: "descartado", label: "Descartado" });
                        const history = detailLead.status_history || [];
                        const currentIdx = steps.findIndex(s => s.value === detailLead.status);
                        const timelineColor = detailLead.status === "convertido" ? "#10b981" : detailLead.status === "descartado" ? "#ef4444" : "#1e293b";
                        return steps.map((step, i) => {
                          const reached = i <= currentIdx;
                          const histEntry = history.find(h => h.status === step.value);
                          const isLast = i === steps.length - 1;
                          return (
                            <div key={step.value} className="flex items-center flex-shrink-0">
                              <div className="flex flex-col items-center min-w-[70px]">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${reached ? "text-white" : "text-slate-400 border-2 border-slate-200"}`}
                                  style={reached ? { backgroundColor: timelineColor } : undefined}>
                                  {reached ? <Check className="w-3.5 h-3.5" /> : i + 1}
                                </div>
                                <p className={`text-[10px] font-semibold mt-1 ${reached ? "" : "text-slate-400"}`} style={reached ? { color: timelineColor } : undefined}>{step.label}</p>
                                {histEntry && <p className="text-[9px] text-slate-400">{fmtDate(histEntry.at)}</p>}
                              </div>
                              {!isLast && <div className="w-8 h-0.5 mx-0.5 mt-[-18px]" style={{ backgroundColor: i < currentIdx ? timelineColor : "#e2e8f0" }} />}
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* Metrics cards */}
                    {metrics && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {/* Time in stage */}
                        <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
                          <Clock className={`w-4 h-4 mx-auto mb-1 ${metrics.stage_urgency === "red" ? "text-rose-500" : metrics.stage_urgency === "yellow" ? "text-amber-500" : "text-emerald-500"}`} />
                          <p className={`text-lg font-bold ${metrics.stage_urgency === "red" ? "text-rose-600" : metrics.stage_urgency === "yellow" ? "text-amber-600" : "text-emerald-600"}`}>
                            {metrics.days_in_stage} {metrics.days_in_stage === 1 ? "dia" : "dias"}
                          </p>
                          <p className="text-[10px] text-slate-400">Na etapa atual</p>
                        </div>
                        {/* Last interaction */}
                        <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
                          <MessageCircle className={`w-4 h-4 mx-auto mb-1 ${metrics.days_since_interaction > 3 ? "text-rose-500" : "text-emerald-500"}`} />
                          <p className={`text-lg font-bold ${metrics.days_since_interaction > 3 ? "text-rose-600" : "text-slate-800"}`}>
                            {metrics.days_since_interaction === 0 ? "Hoje" : `${metrics.days_since_interaction} ${metrics.days_since_interaction === 1 ? "dia" : "dias"}`}
                          </p>
                          <p className="text-[10px] text-slate-400">Última interação</p>
                        </div>
                        {/* Activity */}
                        <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
                          <StickyNote className="w-4 h-4 mx-auto mb-1" style={{ color: tc }} />
                          <p className="text-lg font-bold text-slate-800">{metrics.note_count + metrics.wa_count}</p>
                          <p className="text-[10px] text-slate-400">{metrics.note_count} notas · {metrics.wa_count} msgs</p>
                        </div>
                        {/* Temperature */}
                        <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
                          {metrics.temperature === "quente" ? <Flame className="w-4 h-4 mx-auto mb-1 text-rose-500" /> :
                           metrics.temperature === "morno" ? <Zap className="w-4 h-4 mx-auto mb-1 text-amber-500" /> :
                           <Snowflake className="w-4 h-4 mx-auto mb-1 text-blue-500" />}
                          <p className={`text-lg font-bold ${metrics.temperature === "quente" ? "text-rose-600" : metrics.temperature === "morno" ? "text-amber-600" : "text-blue-600"}`}>
                            {metrics.temperature === "quente" ? "Quente" : metrics.temperature === "morno" ? "Morno" : "Frio"}
                          </p>
                          <p className="text-[10px] text-slate-400">Score: {metrics.temp_score}/100</p>
                        </div>
                      </div>
                    )}

                    {/* Next step suggestion */}
                    {metrics?.suggestion && (
                      <div className="flex items-center gap-3 p-3 rounded-lg border-2" style={{ borderColor: `${tc}40`, backgroundColor: `${tc}08` }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${tc}20` }}>
                          <Zap className="w-4 h-4" style={{ color: tc }} />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold" style={{ color: tc }}>Próximo passo sugerido</p>
                          <p className="text-sm text-slate-700">{metrics.suggestion.text}</p>
                        </div>
                      </div>
                    )}

                    {/* Contact data */}
                    <div className="grid grid-cols-2 gap-4">
                      {detailLead.whatsapp && <div><Label className="text-xs text-slate-400">WhatsApp</Label><p className="text-sm font-medium text-slate-800">{formatPhone(detailLead.whatsapp)}</p></div>}
                      {detailLead.email && <div><Label className="text-xs text-slate-400">Email</Label><p className="text-sm font-medium text-slate-800">{detailLead.email}</p></div>}
                      {detailLead.empresa && <div><Label className="text-xs text-slate-400">Empresa</Label><p className="text-sm font-medium text-slate-800">{detailLead.empresa}</p></div>}
                      <div><Label className="text-xs text-slate-400">Responsável</Label><p className="text-sm font-medium text-slate-800">{detailLead.responsavel_nome || "Sem responsável"}</p></div>
                      <div><Label className="text-xs text-slate-400">Criado em</Label><p className="text-sm text-slate-600">{fmtFull(detailLead.created_at)}</p></div>
                      {detailLead.updated_at && <div><Label className="text-xs text-slate-400">Atualizado em</Label><p className="text-sm text-slate-600">{fmtFull(detailLead.updated_at)}</p></div>}
                    </div>
                    {customFields.length > 0 && Object.keys(detailLead.custom_fields || {}).length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {customFields.map(cf => {
                          const val = detailLead.custom_fields?.[cf.field_name];
                          if (val === null || val === undefined || val === "") return null;
                          return (<div key={cf.field_name}><p className="text-[10px] text-slate-400">{cf.field_label}</p><p className="text-sm text-slate-700">{Array.isArray(val) ? val.join(", ") : String(val)}</p></div>);
                        })}
                      </div>
                    )}
                  </div>
                )}

                {detailTab === "editar" && can("leads", "edit") && detailLead.status !== "convertido" && detailLead.status !== "descartado" && (
                  <div className="flex flex-col h-full">
                    <div className="flex-1 space-y-3 overflow-y-auto">
                      <div><Label>Nome do contato *</Label><Input value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>WhatsApp</Label><Input value={formatPhone(form.whatsapp)} onChange={e => setForm(p => ({ ...p, whatsapp: e.target.value.replace(/\D/g, "") }))} placeholder="(00) 00000-0000" /></div>
                        <div><Label>Email</Label><Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} type="email" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Empresa</Label><Input value={form.empresa} onChange={e => setForm(p => ({ ...p, empresa: e.target.value }))} /></div>
                        <div>
                          <Label>Responsável</Label>
                          <Select value={form.responsavel_id || "__none__"} onValueChange={v => setForm(p => ({ ...p, responsavel_id: v === "__none__" ? "" : v }))}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Nenhum</SelectItem>
                              {responsaveis.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {customFields.length > 0 && (
                        <CustomFieldsForm fields={customFields} values={form.custom_fields} onChange={cf => setForm(p => ({ ...p, custom_fields: cf }))} />
                      )}
                    </div>
                    {/* Footer — save button fixed at bottom */}
                    <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-slate-200 flex-shrink-0">
                      <Button onClick={async () => {
                        if (form.email && !isValidEmail(form.email)) { toast.error("Email inválido. Verifique o formato."); return; }
                        setSaving(true);
                        try {
                          await axios.put(`${API}/leads/${detailLead.id}`, form);
                          toast.success("Lead atualizado!");
                          const updated = await axios.get(`${API}/leads/${detailLead.id}`);
                          setDetailLead(updated.data);
                          fetchLeads();
                        } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
                        setSaving(false);
                      }} disabled={saving} className="text-white" style={{ backgroundColor: "#1e293b" }}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Salvar
                      </Button>
                    </div>
                  </div>
                )}

                {detailTab === "anotacoes" && (
                  <div className="space-y-4">
                    {(() => {
                      const isClosed = detailLead.status === "convertido" || detailLead.status === "descartado";
                      return (
                      <>
                    {notes.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                        <StickyNote className="w-8 h-8 mb-2 text-slate-300" />
                        <p className="text-sm">Nenhuma anotação neste lead.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {notes.map(note => (
                          <div key={note.id}
                            onClick={() => { if (!isClosed) { setEditingNote(note); setNoteEditorOpen(true); } }}
                            className={`rounded-lg border border-slate-200 bg-white p-3 transition-all duration-200 group ${!isClosed ? "cursor-pointer" : ""}`}
                            onMouseEnter={e => e.currentTarget.style.borderColor = "#1e293b"}
                            onMouseLeave={e => e.currentTarget.style.borderColor = ""}>
                            <div className="prose prose-sm max-w-none text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: note.corpo_html }} />
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                              <div className="flex items-center gap-4 text-[11px] text-slate-400">
                                {note.user_name && (
                                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{note.user_name}</span>
                                )}
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtFull(note.created_at)}</span>
                              </div>
                              {!isClosed && (
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Tooltip><TooltipTrigger asChild>
                                    <button onClick={(e) => { e.stopPropagation(); setEditingNote(note); setNoteEditorOpen(true); }} className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"><Pencil className="w-3 h-3" /></button>
                                  </TooltipTrigger><TooltipContent>Editar</TooltipContent></Tooltip>
                                  <Tooltip><TooltipTrigger asChild>
                                    <button onClick={(e) => { e.stopPropagation(); setDeleteNoteTarget(note); }} className="w-6 h-6 flex items-center justify-center rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500"><Trash2 className="w-3 h-3" /></button>
                                  </TooltipTrigger><TooltipContent>Excluir</TooltipContent></Tooltip>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Note editor */}
                    {!isClosed && (
                      <LeadNoteEditor
                        open={noteEditorOpen}
                        onClose={setNoteEditorOpen}
                        leadId={detailLead.id}
                        tc500={"#1e293b"}
                        editNote={editingNote}
                        onSaved={() => { setNoteEditorOpen(false); setEditingNote(null); fetchNotes(detailLead.id); }}
                      />
                    )}
                    {/* Delete note confirmation */}
                    <AlertDialog open={!!deleteNoteTarget} onOpenChange={v => { if (!v) setDeleteNoteTarget(null); }}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-rose-500" />Excluir anotação</AlertDialogTitle>
                          <AlertDialogDescription>Tem certeza que deseja excluir esta anotação? Esta ação não pode ser desfeita.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel><X className="w-4 h-4 mr-2" />Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={async () => {
                            try {
                              await axios.delete(`${API}/leads/${detailLead.id}/notes/${deleteNoteTarget.id}`);
                              toast.success("Anotação excluída");
                              fetchNotes(detailLead.id);
                            } catch { toast.error("Erro ao excluir"); }
                            setDeleteNoteTarget(null);
                          }} className="bg-rose-600 hover:bg-rose-700 text-white"><Trash2 className="w-4 h-4 mr-2" />Excluir</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                      </>
                      );
                    })()}
                  </div>
                )}

                {detailTab === "whatsapp" && detailLead.whatsapp && (
                    <WhatsAppChat negocio={{ id: detailLead.id, titulo: detailLead.nome }} contact={{ nome: detailLead.nome, tipos_contato: [{ tipo: "Celular", valor: detailLead.whatsapp }] }} themeColor="#1e293b" leadId={detailLead.id} hideHeader onRead={() => {
                      setWaUnread(prev => ({ ...prev, [detailLead.id]: 0 }));
                    }} />
                )}
                </div>
              </div>
              </div>
              {/* end modal content */}
            </div>
            {/* end relative container */}
          </DialogContent>
        </Dialog>
      </>)}

      {/* Delete Dialog — identical to Negocios */}
      <AlertDialog open={!!deleteTarget} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-rose-500" />Excluir Lead</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Tem certeza que deseja excluir <strong>"{deleteTarget?.nome}"</strong>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><X className="w-4 h-4 mr-2" />Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white">
              <Trash2 className="w-4 h-4 mr-2" />Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reopen Dialog — identical to Negocios */}
      <AlertDialog open={!!reopenTarget} onOpenChange={v => { if (!v) setReopenTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-slate-500" />Reabrir Lead</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Tem certeza que deseja reabrir <strong>"{reopenTarget?.nome}"</strong>? O status voltará para "Novo".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><X className="w-4 h-4 mr-2" />Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleReopen} className="bg-slate-800 hover:bg-slate-900 text-white">
              <Layers className="w-4 h-4 mr-2" />Reabrir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View All Data Dialog */}
      <Dialog open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" style={{ color: tc }} />Dados do Lead</DialogTitle></DialogHeader>
          {detailLead && (
            <div className="space-y-2 text-sm">
              {Object.entries(detailLead).filter(([k]) => !["_id", "tenant_id", "deleted", "deleted_at"].includes(k)).map(([k, v]) => (
                <div key={k} className="flex gap-2 py-1 border-b border-slate-50">
                  <span className="text-xs font-mono text-slate-400 w-36 flex-shrink-0">{k}</span>
                  <span className="text-xs text-slate-700 break-all">{typeof v === "object" ? JSON.stringify(v, null, 1) : String(v ?? "—")}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Discard Dialog — identical pattern */}
      <AlertDialog open={!!discardTarget} onOpenChange={v => { if (!v) setDiscardTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ThumbsDown className="w-5 h-5 text-rose-500" />Descartar Lead</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Tem certeza que deseja descartar <strong>"{discardTarget?.nome}"</strong>? O lead será marcado como descartado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><X className="w-4 h-4 mr-2" />Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscard} className="bg-rose-600 hover:bg-rose-700 text-white">
              <ThumbsDown className="w-4 h-4 mr-2" />Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert Dialog */}
      <AlertDialog open={!!convertTarget} onOpenChange={v => { if (!v) { setConvertTarget(null); setExistingDeal(null); setConversionPreview(null); } }}>
        <AlertDialogContent className="max-w-lg">
          <button onClick={() => { setConvertTarget(null); setExistingDeal(null); setConversionPreview(null); }}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ArrowRight className="w-5 h-5 text-emerald-500" />Converter Lead em Negócio</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-slate-500">
                {existingDeal ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    <p className="font-semibold">Negócio existente encontrado!</p>
                    <p className="mt-1">Já existe o negócio <strong>"{existingDeal.titulo}"</strong> associado a este contato. Ao prosseguir, o histórico do lead será adicionado como anotação no negócio existente.</p>
                  </div>
                ) : (
                  <>
                    <p>O lead <strong className="text-slate-700">"{convertTarget?.nome}"</strong> será convertido. Isso irá:</p>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      <li>Criar um <strong>novo negócio</strong> na pipeline selecionada</li>
                      <li>Transferir todo o histórico (anotações, mensagens, transcrições) como anotação no negócio</li>
                      {convertTarget?.empresa && <li>Cadastrar a empresa <strong>"{convertTarget.empresa}"</strong> no módulo Empresas (se ativo)</li>}
                      {(convertTarget?.whatsapp || convertTarget?.email) && <li>Cadastrar o contato com nome{convertTarget?.whatsapp ? ", WhatsApp" : ""}{convertTarget?.email ? ", email" : ""} no módulo Contatos (se ativo)</li>}
                    </ul>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Pipeline de destino *</Label>
                      <Select value={convertPipeline} onValueChange={setConvertPipeline}>
                        <SelectTrigger className="h-9 mt-1"><SelectValue placeholder="Selecionar pipeline..." /></SelectTrigger>
                        <SelectContent>{pipelines.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {/* Conversion preview */}
                {conversionPreview && (
                  <div className="border border-slate-200 rounded-lg max-h-60 overflow-y-auto p-3 bg-slate-50 text-xs text-slate-600">
                    <div dangerouslySetInnerHTML={{ __html: conversionPreview }} />
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex items-center">
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9 mr-auto"
                  onClick={async () => {
                    if (conversionPreview) { setConversionPreview(null); return; }
                    try {
                      const res = await axios.get(`${API}/leads/${convertTarget.id}/conversion-preview`);
                      setConversionPreview(res.data.html);
                    } catch { toast.error("Erro ao carregar preview"); }
                  }}>
                  <Eye className="w-4 h-4 text-slate-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{conversionPreview ? "Fechar preview" : "Ver resumo que será adicionado"}</TooltipContent>
            </Tooltip>
            <AlertDialogCancel disabled={converting} onClick={() => { setConvertTarget(null); setExistingDeal(null); setConversionPreview(null); }}><X className="w-4 h-4 mr-2" />Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvert} disabled={converting || (!existingDeal && !convertPipeline)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              {existingDeal ? "Vincular ao existente" : "Converter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
